import * as React from 'react'
import { b, constFalse, E, O, pipe, RA, RD, s, t } from '@packages/prelude'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCommentText, faStar, faVideo } from '@fortawesome/pro-solid-svg-icons'
import * as domain from '@packages/domain'
import ReactPlayer from 'react-player'
import { Accordion, AccordionCollapse, AudioRecorderWrapper, ButtonContainer, TextContainer } from './styles'
import { AudioPlayer } from '~/components/AudioPlayer'
import * as AudioRecorder from '~/components/AudioRecorder'
import { Loading } from '~/components/Loading'
import { ProposalSwiper } from '~/components/TextProposal'
import { AccordionToggleButton } from '~/components/Buttons'
import { react } from '~/commons'

interface Props {
  segment: domain.AudioSegment.UserUpload
  options: RD.RemoteData<domain.Error.Type, domain.Api.Options.Type>
  currentRecorder: O.Option<string>
  onIsRecording: (key: string) => (isRecording: boolean) => void
  onAccordionSelect: (key: string | null) => void
  onRecordingComplete: (blob: Blob) => void
  onDiscard: () => void
}

export const UserUploadControl: React.FC<Props> = ({
  segment,
  options,
  currentRecorder,
  onIsRecording,
  onAccordionSelect,
  onRecordingComplete,
  onDiscard
}) => {
  const key = segment.fieldId
  const video = segment.video
  const videoPlayerRef = React.useRef<ReactPlayer>(null)

  const onVideoPlayerPaused = React.useCallback(() => {
    pipe(
      videoPlayerRef?.current,
      O.fromNullable,
      O.map(player => player.seekTo(0))
    )
  }, [])

  return (
    <Accordion
      key={key}
      activeKey={pipe(
        currentRecorder,
        O.getOrElse(() => '')
      )}
      onSelect={key => onAccordionSelect(Array.isArray(key) ? key[0] ?? null : key ?? null)}>
      <AudioRecorderWrapper>
        <TextContainer>
          {pipe(
            segment.description,
            t.NonEmptyString.decode,
            E.getOrElse(() => segment.title)
          )}
          {!segment.optional && <FontAwesomeIcon icon={faStar} />}
        </TextContainer>
        <ButtonContainer>
          {pipe(
            segment.audio,
            O.fold(
              () => (
                <AudioRecorder.View
                  key={key}
                  eventKey={key}
                  disabled={pipe(
                    currentRecorder,
                    O.match(constFalse, _key => b.not(s.Eq.equals(_key, key)))
                  )}
                  onIsRecording={onIsRecording(key)}
                  onRecordingComplete={onRecordingComplete}
                  maxRecordingTime={segment.maxDuration}>
                  {pipe(
                    video,
                    O.fold(
                      () => faCommentText,
                      () => faVideo
                    ),
                    icon => (
                      <AccordionToggleButton eventKey={key} iconActive={icon} iconInactive={icon} />
                    )
                  )}
                </AudioRecorder.View>
              ),
              () => react.monoid.empty
            )
          )}
          {pipe(
            segment.objectUrl,
            O.foldMap(react.monoid)(url => <AudioPlayer src={url} onDiscard={onDiscard} />)
          )}
        </ButtonContainer>
      </AudioRecorderWrapper>
      <AccordionCollapse key={key} eventKey={key}>
        {pipe(
          video,
          O.fold(
            () =>
              pipe(
                options,
                RD.match3(
                  () => <Loading.Small />,
                  () => <>Textvorschläge konnten nicht geladen werden</>,
                  options =>
                    pipe(
                      options.proposals,
                      RA.map(({ text }) => text),
                      proposals => <ProposalSwiper proposals={proposals} />
                    )
                )
              ),
            video => (
              <ReactPlayer
                width="100%"
                height="100%"
                url={video.href}
                ref={videoPlayerRef}
                onPause={onVideoPlayerPaused}
                playing={pipe(
                  currentRecorder,
                  O.match(constFalse, _key => s.Eq.equals(_key, key))
                )}
              />
            )
          )
        )}
      </AccordionCollapse>
    </Accordion>
  )
}
